import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AuthResponse,
  ForgotPasswordRequest,
  LoginRequest,
  OkResponse,
  PublicUser,
  RegisterRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
} from '@vedaunion/shared';
import { apiFetch, ApiError } from './api';
import { setAuthToken } from './platform';
import { removeQueryCache } from './persist';

const ME_KEY = ['me'] as const;

interface MeWithPermissions {
  user: PublicUser | null;
  permissions: string[];
}

/**
 * Single source of truth for "is the user logged in?" Returns the current user
 * or null, plus the flat list of permission keys their role has been granted.
 * 401s map to `{ user: null, permissions: [] }`.
 */
export function useMe() {
  return useQuery<MeWithPermissions>({
    queryKey: ME_KEY,
    queryFn: async ({ signal }) => {
      try {
        const res = await apiFetch<{ user: PublicUser; permissions?: string[] } | { user: null }>(
          '/api/me',
          { signal },
        );
        if ('permissions' in res && res.user) {
          return { user: res.user, permissions: res.permissions ?? [] };
        }
        return { user: null, permissions: [] };
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          return { user: null, permissions: [] };
        }
        throw err;
      }
    },
  });
}

/** Returns true if the current user has the given permission key. */
export function useCan(permKey: string): boolean {
  const me = useMe();
  return !!me.data?.permissions.includes(permKey);
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation<AuthResponse, ApiError, RegisterRequest>({
    mutationFn: (body) =>
      apiFetch<AuthResponse>('/api/auth/register', { method: 'POST', body }),
    onSuccess: (data) => {
      // Present only for the packaged app, which has no cookie to receive.
      if (data.token) setAuthToken(data.token);
      qc.setQueryData(ME_KEY, { user: data.user, permissions: [] });
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation<AuthResponse, ApiError, LoginRequest>({
    mutationFn: (body) =>
      apiFetch<AuthResponse>('/api/auth/login', { method: 'POST', body }),
    onSuccess: (data) => {
      // The app stores this in native secure storage and replays it as a bearer
      // token on every later request — that is what makes the session outlive an
      // app restart, and survive being offline. Web callers get no token.
      if (data.token) setAuthToken(data.token);
      qc.setQueryData(ME_KEY, { user: data.user, permissions: [] });
      qc.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation<OkResponse, ApiError, void>({
    mutationFn: () =>
      apiFetch<OkResponse>('/api/auth/logout', { method: 'POST', body: {} }),
    onSuccess: () => {
      setAuthToken(null);
      qc.clear();
      qc.setQueryData(ME_KEY, { user: null, permissions: [] });
      // qc.clear() empties memory; the on-disk snapshot must go too, or the next
      // cold launch rehydrates the signed-out user's library from IndexedDB.
      void removeQueryCache();
    },
  });
}

export function useForgotPassword() {
  return useMutation<OkResponse, ApiError, ForgotPasswordRequest>({
    mutationFn: (body) =>
      apiFetch<OkResponse>('/api/auth/forgot-password', { method: 'POST', body }),
  });
}

export function useResetPassword() {
  return useMutation<OkResponse, ApiError, ResetPasswordRequest>({
    mutationFn: (body) =>
      apiFetch<OkResponse>('/api/auth/reset-password', { method: 'POST', body }),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation<{ user: PublicUser }, ApiError, UpdateProfileRequest>({
    mutationFn: (body) =>
      apiFetch<{ user: PublicUser }>('/api/account/profile', {
        method: 'PATCH',
        body,
      }),
    onSuccess: (data) => {
      const prev = qc.getQueryData<{ user: PublicUser | null; permissions: string[] }>(ME_KEY);
      qc.setQueryData(ME_KEY, { user: data.user, permissions: prev?.permissions ?? [] });
    },
  });
}
